import { makeAutoObservable } from "mobx";

import { CoreMath } from "utils/math/core-math";
import { Vec2 } from "utils/math/vec2";
import { toDegrees } from "utils/math/to-degrees";

export class Transform {
  public readonly size: Vec2;

  public readonly translation: Vec2;

  public readonly rotation: CoreMath; // in degrees
  public get degrees() {
    return this.rotation.value;
  }

  public get rads() {
    return this.rotation.getRadians();
  }

  public get rotatedDimensions(): Vec2 {
    const radian = (this.degrees * Math.PI) / 180;
    const cosTheta = Math.cos(radian);
    const sinTheta = Math.sin(radian);
    const { width, height } = this.size;
    const rotatedWidth = Math.abs(width * cosTheta) + Math.abs(height * sinTheta);
    const rotatedHeight = Math.abs(height * cosTheta) + Math.abs(width * sinTheta);
    return new Vec2(rotatedWidth, rotatedHeight);
  }

  public readonly scale: Vec2;

  constructor() {
    this.size = Vec2.zero();
    this.translation = Vec2.zero();
    this.rotation = CoreMath.zero();
    this.scale = Vec2.one();

    makeAutoObservable(this);
  }

  public clone(): Transform {
    const newTransform = new Transform();
    return newTransform.copy(this);
  }

  public reset(): Transform {
    this.setSize(Vec2.zero());
    this.setTranslation(Vec2.zero());
    this.setDegrees(0);
    this.setScale(Vec2.one());
    return this;
  }

  public equals(to: Transform): boolean {
    const sameSize = this.size.equals(to.size);
    const sameTranslation = this.translation.equals(to.translation);
    const sameDegrees = this.degrees === to.degrees;
    const sameScale = this.scale.equals(to.scale);
    return sameSize && sameTranslation && sameDegrees && sameScale;
  }

  public copy(from: Transform): Transform {
    this.setSize(from.size);
    this.setTranslation(from.translation);
    this.setDegrees(from.rotation.value);
    this.setScale(from.scale);
    return this;
  }

  public setSize(width: number, height: number): void;
  public setSize(vec2: Vec2): void;
  public setSize(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const width = isVec ? a.x : a;
    const height = isVec ? a.y : b;
    this.size.set(width, height);
  }

  public resize(width: number, height: number): void;
  public resize(vec2: Vec2): void;
  public resize(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const width = isVec ? a.x : a;
    const height = isVec ? a.y : b;
    this.size.add(width, height);
  }

  public setTranslation(x: number, y: number): void;
  public setTranslation(vec2: Vec2): void;
  public setTranslation(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;
    this.translation.set(x, y);
  }

  public translate(x: number, y: number): void;
  public translate(vec2: Vec2): void;
  public translate(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;
    this.translation.add(x, y);
  }

  public setDegrees(degrees: number) {
    this.rotation.set(degrees).modulo(360);
  }

  public setRads(rads: number) {
    this.rotation.set(toDegrees(rads)).modulo(360);
  }

  public rotateDegrees(degrees: number) {
    this.rotation.add(degrees).modulo(360);
  }

  public rotateRads(rads: number) {
    this.rotation.add(toDegrees(rads)).modulo(360);
  }

  public setScale(x: number, y: number): void;
  public setScale(vec2: Vec2): void;
  public setScale(a: any, b?: any) {
    const isVec = a instanceof Vec2;
    const x = isVec ? a.x : a;
    const y = isVec ? a.y : b;
    this.scale.set(x, y);
  }

  public calcDimensions(options: {
    position: Vec2;
    oX?: number;
    oY?: number;
    roundPosition?: boolean;
    roundSize?: boolean;
    rotation?: number;
    size?: Vec2;
    scale?: Vec2;
  }) {
    const {
      position,
      oX = 0.5,
      oY = 0.5,
      roundPosition = true,
      roundSize = true,
      rotation = this.rotation.value,
      size = this.size,
      scale = this.scale,
    } = options;
    const sizeToSet = size.clone();
    if (roundSize) sizeToSet.round();

    const translationOffset = sizeToSet.clone().multiply(oX, oY);
    const translationToSet = translationOffset
      .negate()
      .add(position)
      .rotateDegrees(position, -rotation.valueOf());
    if (roundPosition) translationToSet.round();

    this.setSize(sizeToSet);
    this.setTranslation(translationToSet);
    this.setDegrees(rotation);
    this.setScale(scale);
  }

  public logTransform(name?: string) {
    const size = `size: ${this.size.width}x${this.size.height}`;
    const translation = `translation: ${this.translation.x}x${this.translation.y}`;
    const rotation = `rotation: ${this.rotation.value}deg`;
    const scale = `scale: ${this.scale.x}x${this.scale.y}`;

    // eslint-disable-next-line no-console
    console.log(`${name}:\n${size}\n${translation}\n${rotation}\n${scale}`);
  }
}
